import { useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";
import { formatDate } from "~/utils/helpers";

export const useOrderStatusChange = (onSuccess: () => Promise<void>) => {
  const [isLoadingTransition, setIsLoadingTransition] = useState<string | null>(
    null
  );
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<SalesRequest | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<SalesRequest[]>([]);
  const profileStore = useProfileStore();

  const setOrderInTransit = async (order: SalesRequest) => {
    setIsLoadingTransition(order.id);
    const updates = {
      status: "in_transit" as const,
      updated_at: new Date().toISOString(),
    };

    try {
      const { error } = await supabase
        .from("sales_request")
        .update(updates)
        .eq("id", order.id);

      if (error) throw error;

      // Update the auto-generated history entry to show "facturado" since no transportista is assigned yet
      // The trigger creates an entry with new_status='in_transit', we change it to 'facturado'
      const { data: latestHistory } = await supabase
        .from("sales_request_status_history")
        .select("id")
        .eq("sales_request_id", order.id)
        .eq("new_status", "in_transit")
        .order("changed_at", { ascending: false })
        .limit(1)
        .single();

      if (latestHistory) {
        await supabase
          .from("sales_request_status_history")
          .update({ new_status: "facturado" })
          .eq("id", latestHistory.id);
      }

      // Send email notification
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL || "https://mdrvhekhimhcwydrpueo.supabase.co"}/functions/v1/notify-intransit`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sales_request_id: order.id }),
          }
        );
        const result = await resp.json();
        if (resp.ok && result.message === "Notification sent") {
          toast({
            title: "Estado actualizado",
            description:
              "La nota está ahora En tránsito y el vendedor fue notificado.",
          });
        } else {
          toast({
            title: "Estado actualizado",
            description:
              "La nota está ahora En tránsito, pero no se pudo notificar al vendedor.",
            variant: "destructive",
          });
        }
      } catch (err: any) {
        toast({
          title: "Estado actualizado",
          description:
            "La nota está ahora En tránsito, pero no se pudo enviar la notificación por email.",
          variant: "destructive",
        });
      }

      await onSuccess();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTransition(null);
    }
  };

  const openDeliveryDialog = (order: SalesRequest) => {
    setSelectedOrder(order);
    setDeliveryDialogOpen(true);
  };

  const confirmDelivery = async (startDate: Date, endDate: Date) => {
    // Cobranza needs the real delivery date (the vencimiento countdown starts
    // from it), not just the timestamp of when the movement was registered.
    // Annotate the auto-created "delivered" history row with the chosen date.
    const deliveryDateLabel = `Fecha de entrega: ${formatDate(startDate, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`;

    // Handle bulk delivery
    if (selectedOrders.length > 0) {
      setIsBulkProcessing(true);
      const updates = {
        status: "delivered" as const,
        updated_at: new Date().toISOString(),
        effective_delivery_date_start: startDate.toISOString(),
        effective_delivery_date_end: endDate.toISOString(),
      };

      let successCount = 0;
      let errorCount = 0;

      for (const order of selectedOrders) {
        try {
          const { error } = await supabase
            .from("sales_request")
            .update(updates)
            .eq("id", order.id);

          if (error) throw error;
          successCount++;

          // Best-effort: a failed annotation must not undo a successful delivery.
          try {
            await supabase.rpc("annotate_latest_status_change", {
              p_sales_request_id: order.id,
              p_new_status: "delivered",
              p_comment: deliveryDateLabel,
            });
          } catch (e) {
            console.error(
              "No se pudo anotar la fecha de entrega en el historial:",
              e
            );
          }
        } catch {
          errorCount++;
        }
      }

      if (errorCount === 0) {
        toast({
          title: "Entregas registradas",
          description: `${successCount} pedidos marcados como entregados.`,
        });
      } else {
        toast({
          title: "Entregas parcialmente registradas",
          description: `${successCount} exitosos, ${errorCount} con errores.`,
          variant: "destructive",
        });
      }

      setDeliveryDialogOpen(false);
      setSelectedOrders([]);
      setIsBulkProcessing(false);
      await onSuccess();
      return;
    }

    // Handle single delivery
    if (!selectedOrder) return;

    setIsLoadingTransition(selectedOrder.id);
    const updates = {
      status: "delivered" as const,
      updated_at: new Date().toISOString(),
      effective_delivery_date_start: startDate.toISOString(),
      effective_delivery_date_end: endDate.toISOString(),
    };

    try {
      const { error } = await supabase
        .from("sales_request")
        .update(updates)
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Best-effort: a failed annotation must not undo a successful delivery.
      try {
        await supabase.rpc("annotate_latest_status_change", {
          p_sales_request_id: selectedOrder.id,
          p_new_status: "delivered",
          p_comment: deliveryDateLabel,
        });
      } catch (e) {
        console.error(
          "No se pudo anotar la fecha de entrega en el historial:",
          e
        );
      }

      toast({
        title: "Entrega registrada",
        description: "La nota ha sido marcada como entregada exitosamente.",
      });

      setDeliveryDialogOpen(false);
      await onSuccess();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "No se pudo registrar la entrega.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTransition(null);
      setSelectedOrder(null);
    }
  };

  const setBulkOrdersInTransit = async (orders: SalesRequest[]) => {
    setIsBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const order of orders) {
      const updates = {
        status: "in_transit" as const,
        updated_at: new Date().toISOString(),
      };

      try {
        const { error } = await supabase
          .from("sales_request")
          .update(updates)
          .eq("id", order.id);

        if (error) throw error;

        // Update the auto-generated history entry to show "facturado"
        const { data: latestHistory } = await supabase
          .from("sales_request_status_history")
          .select("id")
          .eq("sales_request_id", order.id)
          .eq("new_status", "in_transit")
          .order("changed_at", { ascending: false })
          .limit(1)
          .single();

        if (latestHistory) {
          await supabase
            .from("sales_request_status_history")
            .update({ new_status: "facturado" })
            .eq("id", latestHistory.id);
        }

        // Send notification (don't wait, fire and forget for bulk)
        fetch(
          `${import.meta.env.VITE_SUPABASE_URL || "https://mdrvhekhimhcwydrpueo.supabase.co"}/functions/v1/notify-intransit`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sales_request_id: order.id }),
          }
        ).catch(() => {});

        successCount++;
      } catch {
        errorCount++;
      }
    }

    if (errorCount === 0) {
      toast({
        title: "Estado actualizado",
        description: `${successCount} pedidos marcados como Facturado.`,
      });
    } else {
      toast({
        title: "Estado parcialmente actualizado",
        description: `${successCount} exitosos, ${errorCount} con errores.`,
        variant: "destructive",
      });
    }

    setIsBulkProcessing(false);
    await onSuccess();
  };

  const openBulkDeliveryDialog = (orders: SalesRequest[]) => {
    setSelectedOrders(orders);
    setDeliveryDialogOpen(true);
  };

  // One-click "Marcar como entregado" (no delivery-date prompt). Used from the
  // Facturado tab to clear service/finished facturas straight to Entregado.
  const markOrderDelivered = async (order: SalesRequest) => {
    setIsLoadingTransition(order.id);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "delivered",
          updated_at: new Date().toISOString(),
        })
        .eq("id", order.id);

      if (error) throw error;

      // Best-effort: a failed annotation must not undo a successful delivery.
      try {
        await supabase.rpc("annotate_latest_status_change", {
          p_sales_request_id: order.id,
          p_new_status: "delivered",
          p_comment: "Marcado como entregado desde Envíos",
        });
      } catch (e) {
        console.error("No se pudo anotar la entrega en el historial:", e);
      }

      toast({
        title: "Pedido entregado",
        description: "La nota ha sido marcada como entregada.",
      });
      await onSuccess();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "No se pudo marcar como entregado.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTransition(null);
    }
  };

  const markBulkOrdersDelivered = async (orders: SalesRequest[]) => {
    setIsBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const order of orders) {
      try {
        const { error } = await supabase
          .from("sales_request")
          .update({
            status: "delivered",
            updated_at: new Date().toISOString(),
          })
          .eq("id", order.id);

        if (error) throw error;

        // Best-effort: a failed annotation must not undo a successful delivery.
        try {
          await supabase.rpc("annotate_latest_status_change", {
            p_sales_request_id: order.id,
            p_new_status: "delivered",
            p_comment: "Marcado como entregado desde Envíos",
          });
        } catch (e) {
          console.error("No se pudo anotar la entrega en el historial:", e);
        }

        successCount++;
      } catch {
        errorCount++;
      }
    }

    if (errorCount === 0) {
      toast({
        title: "Pedidos entregados",
        description: `${successCount} pedidos marcados como entregados.`,
      });
    } else {
      toast({
        title: "Entregas parcialmente registradas",
        description: `${successCount} exitosos, ${errorCount} con errores.`,
        variant: "destructive",
      });
    }

    setIsBulkProcessing(false);
    await onSuccess();
  };

  return {
    isLoadingTransition,
    isBulkProcessing,
    deliveryDialogOpen,
    setDeliveryDialogOpen,
    selectedOrder,
    setOrderInTransit,
    openDeliveryDialog,
    confirmDelivery,
    setBulkOrdersInTransit,
    openBulkDeliveryDialog,
    markOrderDelivered,
    markBulkOrdersDelivered,
  };
};
